import { type ReactElement, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { BarList, Badge, Button, CheckRow, ColumnChart, DateRangeInput, EmptyState, Field, FormRow, ListTable, Notice, PageHeader, Panel, SegmentedControl, TextInput, type Column, Text, Stack, ChoiceGroup, Row, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  type AdRowView, type AnalyticsListId, type AnalyticsListViews, type ApiError, type CompanyRowView, type CreatedRequestRowView, type CustomerRowView, type DriverKmRowView, type ListMoney, type QuoteRowView,
} from '@vello/api-client';
import {
  BI_EXPORT_POLICY, INITIAL_LIST_FILTERS, LIST_MAX_DAYS, LIST_NEEDS_PERSONAL, LIST_PRESETS, LIST_TAB_ORDER, PERSONAL_POLICY, adsSpendSeries, biErrorView, campaignChoices, customPeriodProblem, driverBars, editDays,
  grantsAllow, hasListFilters, kmText, listFileName, listQuery, mixSeries, periodOf, providerChoices, shownDays, switchList, toggled, type ListFilters, type ListPreset,
} from '../../src/index';
import { useAdmin } from '../session';
import { IdRef } from '../components/code-ref';
import { useConsole } from '../console';
import { downloadText } from '../components/bi/download';
import { ExportDialog, type ExportTarget } from '../components/bi/ExportDialog';
import { usePhoneText } from '../components/phone-text';
import { BiProblem, useBiText } from '../components/bi/bi-ui';
const MAX_DAYS_VARS = { max: LIST_MAX_DAYS } as const;
const today = (): string => new Date().toISOString().slice(0, 10);

type AnyView = AnalyticsListViews[AnalyticsListId];

export function AnalyticsListsScreen(): ReactElement {
  const { runtime, grants, markets, scope } = useAdmin();
  const { toast } = useConsole();
  const text = useBiText();
  const { t, v, lang } = text;
  const phoneOf = usePhoneText();
  const fromId = useId();
  const minId = useId();
  const personalHint = useId();
  const personalWord = grants !== undefined && grantsAllow(grants, PERSONAL_POLICY);
  const exportWord = grants !== undefined && grantsAllow(grants, BI_EXPORT_POLICY);

  const [filters, setFilters] = useState<ListFilters>(INITIAL_LIST_FILTERS);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [view, setView] = useState<AnyView | undefined>(undefined);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [tick, setTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [background, setBackground] = useState<ExportTarget | undefined>(undefined);

  const list = filters.list;
  const needsPersonal = LIST_NEEDS_PERSONAL.has(list);
  const blocked = needsPersonal && !personalWord;
  // D527 review P1: per-driver figures are personal data - without the personal word the driver list is the per-market header only
  const summaryOnly = list === 'driver-km' && !personalWord;
  const query = useMemo(() => listQuery(filters, today(), { paging: true, summaryOnly }), [filters, summaryOnly]);
  const shown = shownDays(filters.preset, filters, today());
  const problem = filters.preset === 'custom' ? customPeriodProblem(filters.from, filters.to) : undefined;

  useEffect(() => {
    setView(undefined);
    setError(undefined);
  }, [query, list]);

  useEffect(() => {
    if (query === undefined || blocked) return undefined;
    let live = true;
    void (async () => {
      const res = await runtime.analyticsLists.list(list, query);
      if (!live) return;
      if (res.ok) {
        setView(res.value);
        setError(undefined);
      } else setError(res.error);
    })();
    return () => {
      live = false;
    };
  }, [runtime, query, list, tick, blocked]);

  const refresh = useCallback((): void => {
    setError(undefined);
    setTick((n) => n + 1);
  }, []);
  // The list stays current by itself (no refresh button): a background tick re-reads the same page and keeps the rows on screen meanwhile.
  useAutoRefresh(() => setTick((n) => n + 1), { intervalMs: AUTO_REFRESH_MS.report, enabled: query !== undefined && !blocked });
  const change = (patch: Partial<ListFilters>): void => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 0 }));
  const money = (m: ListMoney | null): string => (m === null ? '—' : (text.fmt.cell('money', { value: m.amountMinor }, m.currency) ?? '—'));
  const km = (meters: number): string => kmText(meters, lang);
  const int = (n: number): string => text.fmt.count(n);
  const instant = (iso: string): string => new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(iso));
  const marketName = (id: string): string => text.marketName(id);

  const period = periodOf(filters.preset, filters, today());
  const exportCsv = async (part?: 'summary'): Promise<void> => {
    const q = listQuery(filters, today(), { paging: false, summaryOnly });
    if (q === undefined || period === undefined) return;
    setExporting(true);
    const res = await runtime.analyticsLists.exportCsv(list, q, part !== undefined ? { part } : {});
    setExporting(false);
    if (!res.ok) {
      toast({ tone: 'warning', message: t(biErrorView(res.error).messageId) });
      return;
    }
    const name = listFileName(list, period, part);
    toast(downloadText(name, res.value) ? { tone: 'success', message: t('ol.csv.done', { name }) } : { tone: 'warning', message: t('ol.csv.failed') });
  };

  // D594: the list as the source of a background export (CSV or XLSX, scheduled or not): the very filters of the page, no paging; the server plans it again.
  const exportBackground = (): void => {
    const q = listQuery(filters, today(), { paging: false, summaryOnly });
    if (q === undefined || period === undefined) return;
    setBackground({ source: { kind: 'LIST_REPORT', list: { list, ...q } }, title: t('bi.exp.dialog.title') });
  };

  const pager = (v: AnyView) =>
    v.page.total > 0
      ? {
          from: v.page.offset + 1,
          to: v.page.offset + v.rows.length,
          total: v.page.total,
          hasPrev: v.page.offset > 0,
          hasNext: v.page.hasMore,
          onPrev: () => setFilters((f) => ({ ...f, page: Math.max(0, f.page - 1) })),
          onNext: () => setFilters((f) => ({ ...f, page: f.page + 1 })),
          prevLabel: t('ol.prev'),
          nextLabel: t('ol.next'),
          rangeLabel: t('ol.range', { from: int(v.page.offset + 1), to: int(v.page.offset + v.rows.length), total: int(v.page.total) }),
        }
      : undefined;
  const empty = <EmptyState title={t('ol.empty.title')} detail={t('ol.empty.detail')} />;
  const loading = view === undefined && error === undefined && query !== undefined && !blocked;
  const loadingLabel = t('ui.loading');
  const table = <Row,>(v: { readonly rows: readonly Row[] } & AnyView, columns: readonly Column<Row>[], rowKey: (r: Row) => string): ReactElement => (
    <ListTable<Row>
      // D580: a city scope fixes the market, so the market column would repeat it on every row.
      columns={scope.current.city !== undefined ? columns.filter((c) => c.key !== 'market') : columns}
      rows={v.rows}
      rowKey={rowKey}
      loading={false}
      countLabel={t('ol.count', { count: int(v.page.total) })}
      minWidth={880}
      {...(pager(v) !== undefined ? { pagination: pager(v) as NonNullable<ReturnType<typeof pager>> } : {})}
      empty={empty}
    />
  );
  const exportLabels = { menu: t('ol.export.menu'), png: t('ol.export.png'), csv: t('ol.export.csv') };
  const dash = <Text role="caption" tone="secondary">—</Text>;

  // ---- the six tables -----------------------------------------------------------------------------------------
  const body = (): ReactElement => {
    if (view === undefined) return <ListTable<never> columns={[]} rows={[]} rowKey={() => ''} loading loadingLabel={loadingLabel} />;
    switch (view.list) {
      case 'driver-km': {
        const named = view.personal;
        const cols: Column<DriverKmRowView>[] = [
          {
            key: 'pick', header: '', width: 'max-content',
            render: (r) => <CheckRow id={`${fromId}-d-${r.marketId}-${r.driverKey}`} checked={picked.includes(r.driverKey)} onChange={(on) => setPicked((p) => toggled(p, r.driverKey, on))}><span className="vello-sr-only">{t('ol.drivers.pick', { ref: r.name ?? r.driverRef })}</span></CheckRow>,
          },
          { key: 'market', header: t('ol.col.market'), width: '0.9fr', render: (r) => marketName(r.marketId) },
          { key: 'driver', header: t('ol.col.driver'), width: '1.2fr', render: (r) => (named && r.name != null ? <span><strong>{r.name}</strong> <Text role="caption" tone="secondary" mono>{r.driverRef}</Text></span> : <span className="tnum">{r.driverRef}</span>) },
          ...(named ? [{ key: 'vehicle', header: t('ol.col.vehicle'), width: '1.1fr', render: (r: DriverKmRowView) => (r.vehiclePlate != null || r.vehicleModel != null ? <span><span className="tnum">{r.vehiclePlate ?? '—'}</span> <Text role="caption" tone="secondary">{r.vehicleModel ?? ''}</Text></span> : dash) }] : []),
          { key: 'requests', header: t('ol.col.requests'), numeric: true, width: 'max-content', render: (r) => int(r.requests) },
          { key: 'completed', header: t('ol.col.completed'), numeric: true, width: 'max-content', render: (r) => int(r.completed) },
          { key: 'km', header: t('ol.col.km'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{km(r.distanceMeters)}</span> },
          { key: 'fares', header: t('ol.col.fares'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.fares)}</span> },
          { key: 'cash', header: t('ol.col.cash'), numeric: true, width: '1.2fr', render: (r) => (r.cash.rides === 0 ? dash : <span title={t('ol.col.cashDetail', { collected: money(r.cash.collected), commission: money(r.cash.commission), rides: int(r.cash.rides) })} className="tnum">{money(r.cash.collected)}</span>) },
        ];
        return (
          <Stack gap={5}>
            <Panel><Stack gap={3}>
              <Text as="h3" role="title">{t('ol.header.title')}</Text>
              <Stack gap={2} data-testid="ol-header">
                {view.markets.flatMap((m) => m.totals.map((tot) => (
                  <span key={`${m.marketId}-${tot.currency ?? ''}`} className="tnum">
                    {t('ol.header.line', {
                      market: m.name, from: view.scope.from, to: view.scope.to, drivers: int(tot.drivers), requests: int(tot.requests), completed: int(tot.completed), km: km(tot.distanceMeters),
                      fares: money(tot.currency === null ? null : { amountMinor: tot.faresMinor, currency: tot.currency }), cash: money(tot.currency === null ? null : { amountMinor: tot.cashCollectedMinor, currency: tot.currency }),
                    })}
                  </span>
                )))}
                {view.markets.every((m) => m.totals.length === 0) && <Text role="caption" tone="secondary">{t('ol.header.none')}</Text>}
              </Stack>
            </Stack></Panel>
            {!summaryOnly && <Panel>
              <Text as="h3" role="title">{t('ol.chart.km')}</Text>
              <div data-testid="ol-chart"><BarList title={t('ol.chart.km')} items={driverBars(view)} formatValue={(n) => `${new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(n)} km`} loading={loading} loadingLabel={t('ol.chart.loading')} emptyLabel={t('ol.chart.empty')} /></div>
            </Panel>}
            {!summaryOnly && table(view, cols, (r) => `${r.marketId}:${r.driverKey}:${r.fares?.currency ?? ''}`)}
          </Stack>
        );
      }
      case 'customers': {
        const cols: Column<CustomerRowView>[] = [
          { key: 'market', header: t('ol.col.market'), width: '0.8fr', render: (r) => marketName(r.marketId) },
          { key: 'customer', header: t('ol.col.customer'), width: '1.3fr', render: (r) => (r.name != null ? <span><strong>{r.name}</strong> <Text role="caption" tone="secondary" mono>{r.customerRef}</Text></span> : <span className="tnum">{r.customerRef}</span>) },
          { key: 'phone', header: t('ol.col.phone'), width: '1fr', render: (r) => (r.phoneMasked != null ? <span className="tnum">{phoneOf(r.phoneMasked)}</span> : dash) },
          { key: 'requests', header: t('ol.col.requests'), numeric: true, width: 'max-content', render: (r) => int(r.requests) },
          { key: 'completed', header: t('ol.col.completed'), numeric: true, width: 'max-content', render: (r) => int(r.completed) },
          { key: 'cancelled', header: t('ol.col.cancelled'), numeric: true, width: 'max-content', render: (r) => int(r.cancelled) },
          { key: 'first', header: t('ol.col.firstAt'), width: '1fr', render: (r) => <span className="tnum">{instant(r.firstAt)}</span> },
          { key: 'last', header: t('ol.col.lastAt'), width: '1fr', render: (r) => <span className="tnum">{instant(r.lastAt)}</span> },
          { key: 'spend', header: t('ol.col.spend'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.spend)}</span> },
        ];
        return table(view, cols, (r) => `${r.marketId}:${r.customerKey}:${r.spend?.currency ?? ''}`);
      }
      case 'companies': {
        const cols: Column<CompanyRowView>[] = [
          { key: 'market', header: t('ol.col.market'), width: '0.8fr', render: (r) => marketName(r.marketId) },
          { key: 'company', header: t('ol.col.company'), width: '1.6fr', render: (r) => <strong>{r.name}</strong> },
          { key: 'requests', header: t('ol.col.requests'), numeric: true, width: 'max-content', render: (r) => int(r.requests) },
          { key: 'completed', header: t('ol.col.completed'), numeric: true, width: 'max-content', render: (r) => int(r.completed) },
          { key: 'cancelled', header: t('ol.col.cancelled'), numeric: true, width: 'max-content', render: (r) => int(r.cancelled) },
          { key: 'spend', header: t('ol.col.spend'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.spend)}</span> },
        ];
        return (
          <Stack gap={5}>
            <Panel>
              <Text as="h3" role="title">{t('ol.chart.requests')}</Text>
              <div data-testid="ol-chart"><BarList title={t('ol.chart.requests')} items={view.rows.slice(0, 10).map((r) => ({ key: `${r.marketId}:${r.accountId}`, label: r.name, value: r.requests }))} formatValue={int} loading={loading} loadingLabel={t('ol.chart.loading')} emptyLabel={t('ol.chart.empty')} /></div>
            </Panel>
            {table(view, cols, (r) => `${r.marketId}:${r.accountId}:${r.spend?.currency ?? ''}`)}
          </Stack>
        );
      }
      case 'requests': {
        const cols: Column<CreatedRequestRowView>[] = [
          { key: 'when', header: t('ol.col.when'), width: '1fr', render: (r) => <span className="tnum">{instant(r.at)}</span> },
          { key: 'market', header: t('ol.col.market'), width: '0.8fr', render: (r) => marketName(r.marketId) },
          { key: 'kind', header: t('ol.col.kind'), width: '0.8fr', render: (r) => v('business.partner_vertical', r.kind) },
          { key: 'code', header: t('ol.col.code'), width: '0.9fr', render: (r) => (r.code === null ? dash : <span className="tnum">{r.code}</span>) },
          { key: 'status', header: t('ol.col.status'), width: '0.9fr', render: (r) => <Badge tone="neutral">{text.valueText('status', r.status.toUpperCase())}</Badge> },
          { key: 'channel', header: t('ol.col.channel'), width: '0.9fr', render: (r) => text.valueText('channel', r.channel.toUpperCase()) },
          { key: 'company', header: t('ol.col.company'), width: '1.2fr', render: (r) => (r.company === null ? dash : (r.company.name ?? dash)) },
          { key: 'by', header: t('ol.col.createdBy'), width: '1fr', render: (r) => <span className="tnum">{r.operatorRef}</span> },
        ];
        return table(view, cols, (r) => `${r.marketId}:${r.orderId}`);
      }
      case 'quotes': {
        const mix = mixSeries(view);
        const cols: Column<QuoteRowView>[] = [
          { key: 'when', header: t('ol.col.when'), width: '1fr', render: (r) => <span className="tnum">{instant(r.at)}</span> },
          { key: 'market', header: t('ol.col.market'), width: '0.8fr', render: (r) => marketName(r.marketId) },
          { key: 'category', header: t('ol.col.category'), width: '0.9fr', render: (r) => v('ride.category', r.category) },
          { key: 'route', header: t('ol.col.route'), width: '1fr', render: (r) => (r.routeProvider === 'UNKNOWN' ? <Badge tone="neutral">{t('ol.provider.unknown')}</Badge> : <span className="tnum">{r.routeProvider}</span>) },
          { key: 'quoted', header: t('ol.col.quoted'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.total)}</span> },
          { key: 'distance', header: t('ol.col.distance'), numeric: true, width: 'max-content', render: (r) => (r.distanceMeters === null ? dash : <span className="tnum">{km(r.distanceMeters)} km</span>) },
          { key: 'became', header: t('ol.col.became'), width: 'max-content', render: (r) => t(r.becameRide ? 'ol.yes' : 'ol.no') },
        ];
        return (
          <Stack gap={5}>
            <Panel>
              <Text as="h3" role="title">{t('ol.chart.mix')}</Text>
              <div data-testid="ol-chart">
                <ColumnChart
                  title={t('ol.chart.mix')}
                  categories={mix.days}
                  series={mix.series.map((s) => ({ key: s.key, label: s.key === 'UNKNOWN' ? t('ol.provider.unknown') : s.key, values: [...s.values] }))}
                  totalLabel={t('ol.chart.total')}
                  formatValue={int}
                  formatAxis={int}
                  height={220}
                  loading={loading}
                  loadingLabel={t('ol.chart.loading')}
                  emptyLabel={t('ol.chart.empty')}
                  exportLabels={exportLabels}
                  exportName={t('ol.chart.mix')}
                />
              </div>
            </Panel>
            {table(view, cols, (r) => `${r.marketId}:${r.quoteId}`)}
          </Stack>
        );
      }
      case 'ads': {
        const spend = adsSpendSeries(view);
        const major = (minor: number): number => text.fmt.chartValue('money', { value: String(minor) }, spend.currency ?? undefined);
        const cols: Column<AdRowView>[] = [
          { key: 'day', header: t('ol.col.day'), width: '0.9fr', render: (r) => <span className="tnum">{r.day}</span> },
          { key: 'market', header: t('ol.col.market'), width: '0.8fr', render: (r) => marketName(r.marketId) },
          { key: 'campaign', header: t('ol.col.campaign'), width: '1.1fr', render: (r) => <IdRef value={r.campaignId} /> },
          { key: 'merchant', header: t('ol.col.merchant'), width: '1.1fr', render: (r) => r.merchantName ?? <IdRef value={r.merchantId} /> },
          { key: 'budget', header: t('ol.col.budget'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.budget)}</span> },
          { key: 'spent', header: t('ol.col.spent'), numeric: true, width: 'max-content', render: (r) => <span className="tnum" title={money(r.spentThatDay)}>{money(r.spent)}</span> },
          { key: 'balance', header: t('ol.col.balance'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{money(r.balance)}</span> },
          { key: 'exposures', header: t('ol.col.exposures'), numeric: true, width: 'max-content', render: (r) => <span className="tnum">{int(r.chargedExposures)} <Text role="caption" tone="secondary">(+{int(r.chargedThatDay)})</Text></span> },
          { key: 'views', header: t('ol.col.views'), width: 'max-content', render: () => <Badge tone="neutral">{t('ol.notRecorded')}</Badge> },
          { key: 'clicks', header: t('ol.col.clicks'), width: 'max-content', render: () => <Badge tone="neutral">{t('ol.notRecorded')}</Badge> },
        ];
        return (
          <Stack gap={5}>
            <Notice tone="neutral">{t('ol.notRecorded.note')}</Notice>
            <Panel>
              <Text as="h3" role="title">{t('ol.chart.spend')}</Text>
              <div data-testid="ol-chart">
                <ColumnChart
                  title={t('ol.chart.spend')}
                  categories={spend.days}
                  series={[{ key: 'spent', label: t('ol.col.spent'), values: spend.values.map(major) }]}
                  formatValue={(n) => text.fmt.chartText('money', n, spend.currency ?? undefined)}
                  formatAxis={(n) => text.fmt.chartText('money', n, spend.currency ?? undefined)}
                  height={220}
                  loading={loading}
                  loadingLabel={t('ol.chart.loading')}
                  emptyLabel={t('ol.chart.empty')}
                  exportLabels={exportLabels}
                  exportName={t('ol.chart.spend')}
                />
              </div>
            </Panel>
            {table(view, cols, (r) => `${r.marketId}:${r.campaignId}:${r.day}`)}
          </Stack>
        );
      }
    }
  };

  // ---- the filters ------------------------------------------------------------------------------------------
  const providerOptions = list === 'quotes' ? providerChoices(view?.list === 'quotes' ? view : undefined, filters.providers) : [];
  const campaignOptions = list === 'ads' ? campaignChoices(view?.list === 'ads' ? view : undefined, filters.campaigns) : [];
  const toolbar = (
    <Panel>
      <Stack gap={4}>
        <Row gap={4} align="center" wrap>
          <SegmentedControl<string>
            ariaLabel={t('ol.period')}
            value={String(filters.preset)}
            onChange={(id) => change({ preset: Number(id) as ListPreset })}
            options={LIST_PRESETS.map((p) => ({ id: String(p), label: t(`ol.preset.${p}`) }))}
          />
          <DateRangeInput idPrefix={fromId} from={shown.from} to={shown.to} onFromChange={(d) => change(editDays(filters.preset, filters, today(), 'from', d))} onToChange={(d) => change(editDays(filters.preset, filters, today(), 'to', d))} fromLabel={t('ol.from')} toLabel={t('ol.to')} invalid={problem !== undefined} />
        </Row>
        {problem !== undefined && <Notice tone="warning">{t(`ol.problem.${problem}`, MAX_DAYS_VARS)}</Notice>}

        {markets !== undefined && markets.length > 1 && (
          <ChoiceGroup>
            <Text as="legend" role="caption" tone="secondary">{t('ol.markets')}</Text>
            {markets.map((m) => (
              <CheckRow key={m.id} id={`${fromId}-m-${m.id}`} checked={filters.markets.includes(m.id)} onChange={(on) => change({ markets: toggled(filters.markets, m.id, on) })}>{m.name}</CheckRow>
            ))}
            {filters.markets.length === 0 && <Text role="caption" tone="secondary">{t('ol.markets.all')}</Text>}
          </ChoiceGroup>
        )}

        {list === 'driver-km' && !summaryOnly && (
          <>
            <FormRow columns="minmax(0, 10rem)">
              <Field label={t('ol.minKm')} htmlFor={minId}><TextInput id={minId} inputMode="decimal" value={filters.minKm} onChange={(e) => change({ minKm: e.target.value })} /></Field>
            </FormRow>
            <Stack gap={1}>
              <CheckRow id={`${fromId}-personal`} checked={filters.personal} disabled={!personalWord} describedBy={personalHint} onChange={(on) => change({ personal: on })}>{t('ol.personal')}</CheckRow>
              <Text role="caption" tone="secondary" id={personalHint}>{t('ol.personal.hint')}</Text>
            </Stack>
            {(picked.length > 0 || filters.drivers.length > 0) && (
              <Row gap={3} align="center" wrap>
                <Text role="caption" tone="secondary">{t('ol.drivers.kept')}: <span className="tnum">{filters.drivers.length}</span></Text>
                <Button variant="secondary" disabled={picked.length === 0} onClick={() => change({ drivers: picked })}>{t('ol.drivers.keep')}</Button>
                <Button variant="ghost" disabled={filters.drivers.length === 0 && picked.length === 0} onClick={() => { setPicked([]); change({ drivers: [] }); }}>{t('ol.drivers.clear')}</Button>
              </Row>
            )}
          </>
        )}
        {(list === 'customers' || list === 'requests') && (
          <ChoiceGroup>
            <Text as="legend" role="caption" tone="secondary">{t('ol.kind')}</Text>
            {(['ride', 'delivery', 'food'] as const).map((k) => (
              <CheckRow key={k} id={`${fromId}-k-${k}`} checked={filters.kinds.includes(k)} onChange={(on) => change({ kinds: toggled(filters.kinds, k, on) })}>{v('business.partner_vertical', k)}</CheckRow>
            ))}
          </ChoiceGroup>
        )}
        {list === 'customers' && (
          <FormRow columns="minmax(0, 10rem)">
            <Field label={t('ol.minRequests')} htmlFor={minId}><TextInput id={minId} inputMode="numeric" value={filters.minRequests} onChange={(e) => change({ minRequests: e.target.value })} /></Field>
          </FormRow>
        )}
        {list === 'quotes' && providerOptions.length > 0 && (
          <ChoiceGroup>
            <Text as="legend" role="caption" tone="secondary">{t('ol.provider')}</Text>
            {providerOptions.map((p) => (
              <CheckRow key={p} id={`${fromId}-p-${p}`} checked={filters.providers.includes(p)} onChange={(on) => change({ providers: toggled(filters.providers, p, on) })}>{p === 'UNKNOWN' ? t('ol.provider.unknown') : p}</CheckRow>
            ))}
          </ChoiceGroup>
        )}
        {list === 'ads' && campaignOptions.length > 0 && (
          <ChoiceGroup>
            <Text as="legend" role="caption" tone="secondary">{t('ol.campaigns')}</Text>
            {campaignOptions.map((c) => (
              <CheckRow key={c} id={`${fromId}-c-${c}`} checked={filters.campaigns.includes(c)} onChange={(on) => change({ campaigns: toggled(filters.campaigns, c, on) })}>{c}</CheckRow>
            ))}
          </ChoiceGroup>
        )}
        {hasListFilters(filters) && <div><Button variant="ghost" onClick={() => { setPicked([]); setFilters((f) => switchList(f, f.list)); }}>{t('ol.clear')}</Button></div>}
      </Stack>
    </Panel>
  );

  const actions = (
    <>
      {exportWord && <Button variant="secondary" icon="download" disabled={view === undefined || query === undefined || blocked} onClick={exportBackground}>{t('bi.exportBackground')}</Button>}
      {exportWord && list === 'driver-km' && <Button variant="secondary" icon="download" disabled={exporting || view === undefined || query === undefined} onClick={() => void exportCsv('summary')}>{t('ol.csv.summary')}</Button>}
      {exportWord && !summaryOnly && <Button variant="primary" icon="download" disabled={exporting || view === undefined || query === undefined} loading={exporting} onClick={() => void exportCsv()}>{t('ol.csv')}</Button>}
    </>
  );

  return (
    <div>
      <PageHeader title={t('ol.title')} subtitle={t('ol.subtitle')} actions={actions} />
      <Stack gap={5}>
        <SegmentedControl<AnalyticsListId>
          ariaLabel={t('ol.tabs')}
          value={list}
          onChange={(id) => { setPicked([]); setFilters((f) => switchList(f, id)); }}
          options={LIST_TAB_ORDER.map((id) => ({ id, label: v('analytics.list_id', id) }))}
        />
        <Text as="p" role="caption" tone="secondary">{t(`ol.about.${list}`)}</Text>
        {(needsPersonal || (list === 'driver-km' && filters.personal)) && <Notice tone="neutral">{t('ol.personalNote')}</Notice>}
        {summaryOnly && <Notice tone="neutral">{t('ol.driversPersonal')}</Notice>}
        {toolbar}
        {blocked && <Notice tone="neutral">{t('ol.personalBlocked')}</Notice>}
        {error !== undefined && <BiProblem error={error.reasonCode === 'ANALYTICS_PERSONAL_FORBIDDEN' ? { ...biErrorView(error), messageId: 'ol.personalBlocked' } : biErrorView(error)} onRetry={refresh} text={text} />}
        {!blocked && error === undefined && body()}
      </Stack>
      <ExportDialog open={background !== undefined} target={background} onClose={() => setBackground(undefined)} />
    </div>
  );
}
